import { getModel } from '../../model/registry'
import { useState, useEffect } from 'react'
import styled from 'styled-components'
import { getFromLS, saveToLS } from '../../utils/localStore/localStore'
import useKanban from './useKanban'
import KanbanHeader from './KanbanHeader'
import KanbanArea from './KanbanArea'
import Loader from '../generic/Loader'
import useTranslation from '../../hooks/useTranslation'

const LS_KEY = 'kanban'

const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;
    background: ${({ theme }) => theme.area.bg};
`

const EmptyState = styled.div`
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 2rem;
    color: ${({ theme }) => theme.font.label};
    font-size: var(--font-M);
`

const KanbanPage = ({ type }) => {
    const { t } = useTranslation()
    const config = getModel('kanbanViewData')[type]

    if (!config) return <span>No kanban config found for: {type}</span>

    const { header: headerConfig, kanbanArea: areaConfig, emptyLabel } = config
    const selector = headerConfig.selector
    const parentEntity = selector.entity
    const assigneeFilterConfig = headerConfig.assigneeFilter ?? null

    const [selectorValue, setSelectorValue] = useState(() => {
        const id = getFromLS(LS_KEY, type) ?? null
        return { [selector.id]: id, [selector.displayField]: null }
    })

    const [assigneeValue, setAssigneeValue] = useState(
        assigneeFilterConfig
            ? { [assigneeFilterConfig.id]: null, [assigneeFilterConfig.displayField]: null }
            : null
    )

    const selectedParentId = selectorValue[selector.id]
    const assigneeFilterId = assigneeFilterConfig ? (assigneeValue?.[assigneeFilterConfig.id] ?? null) : null

    const handleParentChange = (events) => {
        const newValue = Array.isArray(events)
            ? events.reduce((acc, { target: { name, value } }) => ({ ...acc, [name]: value || null }), {})
            : (events ?? { [selector.id]: null, [selector.displayField]: null })
        setSelectorValue(newValue)
        saveToLS(LS_KEY, type, newValue[selector.id] ?? null)
    }

    const handleAssigneeChange = (events) => {
        if (!assigneeFilterConfig) return
        const newValue = Array.isArray(events)
            ? events.reduce((acc, { target: { name, value } }) => ({ ...acc, [name]: value || null }), {})
            : (events ?? { [assigneeFilterConfig.id]: null, [assigneeFilterConfig.displayField]: null })
        setAssigneeValue(newValue)
    }

    const {
        parentList,
        selectedParent,
        tasksLoading,
        tasksByStatus,
        onDragEnd,
        invalidateTasks,
        nextStackRankForSwimlane
    } = useKanban({
        parentId: selectedParentId,
        parentEntity: selector.entity,
        selectorConfig: selector,
        areaConfig,
        assigneeFilterId,
        assigneeIdField: assigneeFilterConfig?.id ?? null
    })

    // Restore parent name from server when loading from localStorage (name isn't persisted)
    useEffect(() => {
        if (selectedParent && !selectorValue[selector.displayField]) {
            setSelectorValue(prev => ({ ...prev, [selector.displayField]: selectedParent.name }))
        }
    }, [selectedParent])

    // Auto-select first item if none remembered
    useEffect(() => {
        if (!selectedParentId && parentList.length > 0) {
            const first = parentList[0]
            handleParentChange({ [selector.id]: first.id, [selector.displayField]: first.name })
        }
    }, [parentList])

    return (
        <PageWrapper>
            <KanbanHeader
                selectorValue={selectorValue}
                selectedParent={selectedParent}
                selectedParentId={selectedParentId}
                onParentChange={handleParentChange}
                assigneeValue={assigneeValue}
                onAssigneeChange={handleAssigneeChange}
                onTaskCreated={invalidateTasks}
                headerConfig={headerConfig}
                areaConfig={areaConfig}
            />

            {!selectedParentId && (
                <EmptyState>{t(emptyLabel)}</EmptyState>
            )}

            {selectedParentId && tasksLoading && <Loader />}

            {selectedParentId && !tasksLoading && (
                <KanbanArea
                    config={areaConfig}
                    tasksByStatus={tasksByStatus}
                    parentId={selectedParentId}
                    parentEntity={parentEntity}
                    parentPath={areaConfig.parentPath}
                    onDragEnd={onDragEnd}
                    onTaskCreated={invalidateTasks}
                    nextStackRankForSwimlane={nextStackRankForSwimlane}
                />
            )}
        </PageWrapper>
    )
}

export default KanbanPage
